/**
 * "An exam is running" flag (spec §4.5): while it is set the shell hides every
 * nav entry marked `hideDuringExam` (topics, past-exam archive …; the formula
 * sheet stays visible, ruling 6), and the library shows only the formula sheet.
 *
 * Two sources (slice D fix I1):
 * - the session view sets it while mounted:
 *     const { setExamActive } = useExamMode();
 *     useEffect(() => { setExamActive(true); return () => setExamActive(false); }, []);
 * - the server: `GET /api/exam/current` answering with an `in_progress` session. So a student
 *   who leaves the session for "Formler", or opens the formula sheet in a new tab, still gets the
 *   restricted shell until the exam is handed in. The key sits under the exam feature's `["exam"]`
 *   prefix, so its start/submit/abandon invalidations refresh it.
 *
 * The server check never spins forever: it is aborted after `EXAM_CHECK_TIMEOUT_MS`. A timeout or
 * error fails CLOSED (`examError`: guards show a retry state; `retryExamCheck` re-asks) only when
 * the local hint says this browser started an exam (see `examHint.ts`); otherwise the guards let the
 * page through, since exams are self-started mocks and a flaky request must not lock everyone out.
 */
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { api } from "@client/api/client";
import { isCode } from "@client/lib/errors";
import { useAuth } from "./auth";
import { clearExamHint, hasExamHint, setExamHint } from "./examHint";

interface ExamModeValue {
  examActive: boolean;
  /** The server has not answered yet for a signed-in user: guarded pages wait rather than flash. */
  examChecking: boolean;
  /** The server check timed out or failed and there is no earlier answer: guards must stay locked. */
  examError: boolean;
  retryExamCheck: () => void;
  setExamActive: (active: boolean) => void;
}

const ExamModeContext = createContext<ExamModeValue | null>(null);

/** Not `["exam", "current"]`: that key's cached `null` sends the session page away (see Home). */
export const EXAM_SHELL_KEY = ["exam", "shell-active"] as const;

/** How long `GET /api/exam/current` may take before the guards give up waiting and offer a retry. */
export const EXAM_CHECK_TIMEOUT_MS = 8_000;

function useServerExamActive(mounted: boolean): {
  active: boolean;
  checking: boolean;
  error: boolean;
  retry: () => void;
} {
  const { user, isLoading } = useAuth();
  const query = useQuery({
    queryKey: EXAM_SHELL_KEY,
    // The session view already says so while it is mounted, and fetches `/current` itself.
    enabled: Boolean(user) && !mounted,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    // The guards' own retry button replaces react-query's silent backoff (3 x 8 s of spinner).
    retry: false,
    queryFn: async ({ signal }) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), EXAM_CHECK_TIMEOUT_MS);
      const onAbort = () => controller.abort();
      if (signal.aborted) controller.abort();
      signal.addEventListener("abort", onAbort, { once: true });
      try {
        const res = await api.get<{ session: { status: string } }>("/api/exam/current", {
          signal: controller.signal,
        });
        const active = res.session.status === "in_progress";
        // The server answered: keep the local hint in step with it.
        if (active) setExamHint();
        else clearExamHint();
        return active;
      } catch (error) {
        if (isCode(error, "NOT_FOUND")) {
          clearExamHint();
          return false;
        }
        throw error;
      } finally {
        clearTimeout(timer);
        signal.removeEventListener("abort", onAbort);
      }
    },
  });
  const on = Boolean(user) && !mounted;
  const unanswered = on && query.data === undefined;
  const { refetch } = query;
  return {
    active: on && query.data === true,
    checking: (isLoading && !mounted) || (unanswered && (query.isPending || query.isFetching)),
    // Lock on a failed check only for a student who started an exam here; others pass through.
    error: unanswered && query.isError && !query.isFetching && hasExamHint(),
    retry: () => void refetch(),
  };
}

/** Must sit inside `<AuthProvider>` and the query client. */
export function ExamModeProvider({ children }: { children: ReactNode }) {
  const [mounted, setExamActive] = useState(false);
  const server = useServerExamActive(mounted);
  const examActive = mounted || server.active;
  const examChecking = server.checking;
  const examError = server.error;
  const retryExamCheck = server.retry;
  const value = useMemo(
    () => ({ examActive, examChecking, examError, retryExamCheck, setExamActive }),
    // `retryExamCheck` closes over a stable `refetch`; re-creating it per render must not churn consumers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [examActive, examChecking, examError],
  );
  return <ExamModeContext.Provider value={value}>{children}</ExamModeContext.Provider>;
}

export function useExamMode(): ExamModeValue {
  const ctx = useContext(ExamModeContext);
  if (!ctx) throw new Error("useExamMode must be used inside <ExamModeProvider>");
  return ctx;
}
